import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { isFieldVisible } from '../../isFieldVisible';

export const MetaLayoutContext = React.createContext(null);

export function MetaLayoutContextProvider({
  children,
  onChange,
  resource,
  errors,
  unsourcedErrors,
  onSubmit,
  onDelete,
  inlineSubmit,
  metaOverrideSet,
  onAdd,
  onRemove,
  saving,
  applyMetaOverrideSet,
  autoSave,
}) {
  // Can't be memoized because resource will stay stable when its attributes change, meaning excludedUnless wouldn't be able to be evaluated again
  const allVisibleFields = !resource
    ? []
    : resource
      .meta()
      .template()
      // This function does several important things:
      // 1. Orders the list
      // 2. Allows meta overrides to do things like make a field hidden/readonly now, before the filter below uses the results
      // 3. Makes a copy of the array and a shallow copy of each field in it, so we don't have to worry about mutating the fields
      .getListOrderedByOrdinal(metaOverrideSet)
      // This filter serves one simple purpose: remove any fields that would not be visible to the end user, after accounting for all the ways it's possible, starting with excludedUnless which affects some of the other ways
      .filter(fieldBeforeExcludedUnless => {
        const field = fieldBeforeExcludedUnless.applyExcludedUnless(fieldBeforeExcludedUnless.attr('name'), resource);
        return isFieldVisible(resource, field);
      })
      .map(f => f.id());

  const [explicitlyRenderedFields, setExplicitlyRenderedFields] = useState(new Set());

  const remainingFields = new Set(allVisibleFields.filter(f => !explicitlyRenderedFields.has(f)));

  const markFieldExplicitlyRendered = React.useCallback((attrName) => setExplicitlyRenderedFields(currentVal => {
    if (currentVal.has(attrName)) {
      return currentVal; // Since this is the exact same set (same place in memory), React will treat this as no state change occurring
    }
    currentVal.add(attrName);
    return new Set(currentVal); // If you don't do this, React will not know the set has changed and will not trigger any re-rendering from this
  }), []);

  // consumeAttr lets you mark a field as having been rendered (meaning MetaLayoutRemainingFields will not render it again) but without rendering it explicitly.
  // For example, if you want to have 1 UI control that is fed by 2 attributes (like PageHeader), you can use this function to get their values while making sure these fields aren't rendered by MetaLayoutRemainingFields
  const consumeAttr = (attrName) => {
    markFieldExplicitlyRendered(attrName);
    const val = resource.attr(attrName);
    if (val && typeof val === 'object' && val.resources) {
      return val.resources().list().map(res => res.export());
    }
    return val;
  };

  const getError = React.useCallback(
    (attrName) => {
      return errors.find(err => {
        if (!err.source) { return false; }
        return err.source.split('[')[0] === attrName;
      });
    },
    [errors],
  );

  return (
    <MetaLayoutContext.Provider
      value={{
        markFieldExplicitlyRendered,
        consumeAttr,
        remainingFields,
        metaOverrideSet,
        handleChange: onChange,
        resource,
        getError,
        onSubmit,
        onDelete,
        inlineSubmit,
        onAdd,
        onRemove,
        errors,
        unsourcedErrors,
        saving,
        applyMetaOverrideSet,
        autoSave,
      }}
    >
      {children}
    </MetaLayoutContext.Provider>
  );
}

MetaLayoutContextProvider.defaultProps = {
  errors: [],
  unsourcedErrors: [],
  onSubmit: null,
  onDelete: null,
  inlineSubmit: false,
  onAdd: null,
  onRemove: null,
  metaOverrideSet: null,
  saving: false,
  applyMetaOverrideSet: false,
  autoSave: false,
};

MetaLayoutContextProvider.propTypes = {
  saving: PropTypes.bool,
  errors: PropTypes.array,
  unsourcedErrors: PropTypes.array,
  children: PropTypes.any.isRequired,
  resource: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  onDelete: PropTypes.func,
  inlineSubmit: PropTypes.bool,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  metaOverrideSet: PropTypes.object,
  applyMetaOverrideSet: PropTypes.bool,
  autoSave: PropTypes.bool,
};

function useMetaLayoutContext() {
  const context = React.useContext(MetaLayoutContext);

  if (!context) {
    throw new Error(
      'useMetaLayoutContext must be called from a MetaLayoutContext.Provider',
    );
  }

  return context;
}

// The reason we're breaking from the norm here is that an error list may be rendered outside of a meta layout context provider, and we want to use the meta layout context provider for its resource if it exists but not blow up if there is no provider nearby.
export function useMaybeMetaLayoutContext() {
  return React.useContext(MetaLayoutContext);
}

export default useMetaLayoutContext;
